import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  QrCode, 
  Plus, 
  ArrowLeft, 
  DotsThree,
  Trash,
  PencilSimple,
  ChartLine,
  Link as LinkIcon,
  Copy,
  Download,
  FileZip
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/App";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

export default function ProjectDetailPage() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [qrcodes, setQrcodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projectRes, qrcodesRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/qrcodes?project_id=${projectId}`),
      ]);
      setProject(projectRes.data);
      setQrcodes(qrcodesRes.data);
    } catch (error) {
      toast.error("Error al cargar el proyecto");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQR = async (qrId) => {
    if (!window.confirm("¿Eliminar este código QR?")) return;
    
    try {
      await api.delete(`/qrcodes/${qrId}`);
      setQrcodes(qrcodes.filter((qr) => qr.qr_id !== qrId));
      toast.success("Código QR eliminado");
    } catch (error) {
      toast.error("Error al eliminar el código QR");
    }
  };

  const copySlugUrl = (slug) => {
    const url = `${window.location.origin}/r/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copiada al portapapeles");
  };

  const downloadQR = async (qrId, format, slug) => {
    try {
      const response = await api.get(`/qrcodes/${qrId}/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'zip' ? `qr-${slug}-assets.zip` : `qr-${slug}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} descargado`);
    } catch (error) {
      toast.error("Error en la descarga");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#05152E' }}>
        <div className="w-12 h-12 border-4 border-t-transparent animate-spin" style={{ borderColor: '#008AD8', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen dark-dashboard" style={{ backgroundColor: '#05152E' }} data-testid="project-detail">
      {/* Header */}
      <header className="border-b sticky top-0 z-50" style={{ backgroundColor: '#05152E', borderColor: '#1A3A5C' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-btn"
              onClick={() => navigate("/dashboard")}
              className="p-2 hover:bg-[#0D2847] transition-colors"
              style={{ color: '#A1A1AA' }}
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <div>
              <p className="uppercase text-[10px] tracking-[0.1em] font-medium" style={{ color: '#71717A' }}>Proyecto</p>
              <h1 className="text-xl font-black text-white">
                {project?.name}
              </h1>
            </div>
          </div>
          
          <button
            data-testid="new-qr-btn"
            onClick={() => navigate(`/qr/new?project=${projectId}`)}
            className="flex items-center gap-2 text-sm px-6 py-3 bg-white font-semibold border-2 border-white hover:bg-[#F4F4F5] transition-colors"
            style={{ color: '#003594', boxShadow: '4px 4px 0px 0px #008AD8' }}
          >
            <Plus size={18} weight="bold" />
            Nuevo Código QR
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Project Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-0 border mb-8" style={{ borderColor: '#1A3A5C' }}>
          <div className="p-6 border-r" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Códigos QR</p>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {project?.qr_count || 0}
            </p>
          </div>
          <div className="p-6 lg:border-r" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Total Escaneos</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#008AD8' }}>
              {project?.total_scans || 0}
            </p>
          </div>
          <div className="p-6 col-span-2 lg:col-span-1 border-t lg:border-t-0" style={{ backgroundColor: '#0A1F3D', borderColor: '#1A3A5C' }}>
            <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Analíticas del Proyecto</p>
            <button
              onClick={() => navigate(`/analytics?project=${projectId}`)}
              className="font-medium hover:underline flex items-center gap-1"
              style={{ color: '#008AD8' }}
            >
              Ver Detalles
              <ChartLine size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* QR Codes Grid */}
        {qrcodes.length === 0 ? (
          <div className="border border-dashed p-12 text-center" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
            <QrCode size={48} weight="duotone" className="mx-auto mb-4" style={{ color: '#1A3A5C' }} />
            <h3 className="font-bold text-lg text-white mb-2">
              Sin códigos QR aún
            </h3>
            <p className="mb-4" style={{ color: '#71717A' }}>
              Crea tu primer código QR para este proyecto
            </p>
            <button
              onClick={() => navigate(`/qr/new?project=${projectId}`)}
              className="px-6 py-3 bg-transparent text-white font-semibold border transition-colors"
              style={{ borderColor: '#1A3A5C' }}
            >
              Crear Código QR
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {qrcodes.map((qr) => (
              <div 
                key={qr.qr_id}
                data-testid={`qr-card-${qr.qr_id}`}
                className="border p-6"
                style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* QR Preview */}
                  <div 
                    className="w-32 h-32 flex-shrink-0 mx-auto lg:mx-0 paper-texture flex items-center justify-center"
                    style={{ backgroundColor: qr.background_color }}
                  >
                    <QRPreview qrId={qr.qr_id} />
                  </div>
                  
                  {/* QR Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Slug</p>
                        <p className="text-xl font-bold text-white" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          /{qr.slug}
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 hover:bg-[#0D2847] transition-colors" style={{ color: '#A1A1AA' }}>
                            <DotsThree size={24} weight="bold" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none border" style={{ borderColor: '#1A3A5C', backgroundColor: '#0A1F3D' }}>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/qr/${qr.qr_id}/edit`)}
                            className="rounded-none cursor-pointer hover:bg-[#0D2847]"
                            style={{ color: '#A1A1AA' }}
                          >
                            <PencilSimple size={16} className="mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate(`/analytics/${qr.qr_id}`)}
                            className="rounded-none cursor-pointer hover:bg-[#0D2847]"
                            style={{ color: '#A1A1AA' }}
                          >
                            <ChartLine size={16} className="mr-2" />
                            Analíticas
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDeleteQR(qr.qr_id)}
                            className="rounded-none cursor-pointer hover:bg-[#0D2847] text-red-400"
                          >
                            <Trash size={16} className="mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="mb-4">
                      <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>URL de Destino</p>
                      <div className="flex items-center gap-2">
                        <LinkIcon size={16} className="flex-shrink-0" style={{ color: '#71717A' }} />
                        <p className="text-sm truncate" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#A1A1AA' }}>
                          {qr.destination_url}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        data-testid={`copy-url-${qr.qr_id}`}
                        onClick={() => copySlugUrl(qr.slug)}
                        className="flex items-center gap-2 px-3 py-2 border transition-colors text-sm"
                        style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                      >
                        <Copy size={16} />
                        Copiar URL
                      </button>
                      <button
                        data-testid={`download-zip-${qr.qr_id}`}
                        onClick={() => downloadQR(qr.qr_id, 'zip', qr.slug)}
                        className="flex items-center gap-2 px-3 py-2 text-white transition-colors text-sm font-semibold"
                        style={{ backgroundColor: '#008AD8' }}
                      >
                        <FileZip size={16} weight="bold" />
                        Descargar Todo
                      </button>
                      <button
                        data-testid={`download-svg-${qr.qr_id}`}
                        onClick={() => downloadQR(qr.qr_id, 'svg', qr.slug)}
                        className="flex items-center gap-2 px-3 py-2 border transition-colors text-sm"
                        style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                      >
                        <Download size={16} />
                        SVG
                      </button>
                      <button
                        data-testid={`download-png-${qr.qr_id}`}
                        onClick={() => downloadQR(qr.qr_id, 'png', qr.slug)}
                        className="flex items-center gap-2 px-3 py-2 border transition-colors text-sm"
                        style={{ borderColor: '#1A3A5C', color: '#A1A1AA' }}
                      >
                        <Download size={16} />
                        PNG
                      </button>
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex lg:flex-col items-center gap-4 lg:gap-6 justify-center lg:border-l lg:pl-6" style={{ borderColor: '#1A3A5C' }}>
                    <div className="text-center">
                      <p className="uppercase text-[10px] tracking-[0.1em] font-medium mb-1" style={{ color: '#71717A' }}>Escaneos</p>
                      <p className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#008AD8' }}>
                        {qr.scan_count || 0}
                      </p>
                    </div>
                    <div 
                      className="w-6 h-6 border"
                      style={{ backgroundColor: qr.foreground_color, borderColor: '#1A3A5C' }}
                      title={`Color: ${qr.foreground_color}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// QR Preview Component
function QRPreview({ qrId }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await api.get(`/qrcodes/${qrId}/preview`);
        setPreview(response.data.preview);
      } catch (error) {
        console.error("Error al cargar vista previa");
      }
    };
    fetchPreview();
  }, [qrId]);

  if (!preview) {
    return <div className="w-24 h-24 animate-pulse" style={{ backgroundColor: '#0D2847' }}></div>;
  }

  return (
    <img 
      src={preview} 
      alt="Código QR" 
      className="w-28 h-28 object-contain qr-animate-in"
    />
  );
}
